import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { track } from "../lib/analytics";
import useEyeTracking from "../hooks/useEyeTracking";
import useCartesiaTTS from "../hooks/useCartesiaTTS";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Mic, MicOff, Video, AlertTriangle, Check, Volume2, Download, Beaker, Type, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { computeDelivery, computeDeliveryScore, getMetricColor, COLOR_CLASSES } from "../lib/speechDelivery";
import { useProgressSteps } from "../hooks/useProgressSteps";
import { ProgressSteps } from "../components/ui/ProgressSteps";

const FINISH_STEPS = [
  { label: "Submitting answers", duration: 2500 },
  { label: "Evaluating responses", duration: 6000 },
  { label: "Calculating scores", duration: 5000 },
  { label: "Preparing results", duration: 0 },
];

const MODULE_ORDER = ["hr", "technical", "behavioral", "custom"];
const MODULE_LABEL = { hr: "HR Round", technical: "Technical", behavioral: "Behavioral", custom: "Custom" };
const ACKNOWLEDGMENTS = ["Thank you.", "Got it.", "Good.", "Understood."];

const CHEAT_MESSAGES = {
  looking_down:    { label: "EYES LOOKING DOWN",        detail: "Keep your eyes on the screen — this has been logged." },
  tab_switch:      { label: "TAB SWITCH DETECTED",      detail: "Switching browser tabs has been logged." },
  window_blur:     { label: "FOCUS LOST",               detail: "Leaving the interview window has been logged." },
  multiple_faces:  { label: "MULTIPLE PEOPLE DETECTED", detail: "Another person in frame has been logged." },
  face_missing:    { label: "FACE NOT VISIBLE",         detail: "Return to camera view — absence has been logged." },
  attention_drift: { label: "ATTENTION DRIFT",          detail: "Keep your eyes on the screen — this has been logged." },
};

function ExitConfirmOverlay({ qIdx, moduleIdx, onStay, onExit }) {
  const anyAnswered = qIdx > 0 || moduleIdx > 0;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6">
      <div className="bg-[#111] rounded-lg border border-[#262626] p-8 max-w-sm w-full">
        <div className="font-mono text-[10px] text-white/40 tracking-[0.25em] mb-3 uppercase">Exit Interview</div>
        <h2 className="font-display font-bold text-xl text-white mb-2">Leave this session?</h2>
        <p className="text-white/50 text-sm leading-relaxed mb-7">
          {anyAnswered
            ? "Your answered questions will be graded and saved to your results report."
            : "No answers have been submitted yet. You'll return to your dashboard."}
        </p>
        <div className="flex gap-3">
          <button onClick={onStay}
            className="flex-1 py-3 rounded-lg border border-[#333] text-white/60 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1a1a1a] transition-colors">
            Stay
          </button>
          <button onClick={onExit}
            className="flex-1 py-3 bg-[#E11D48] hover:bg-[#BE123C] text-white font-mono text-xs uppercase tracking-[0.2em] transition-colors">
            {anyAnswered ? "Exit & save" : "Exit"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeliveryCard({ metrics, onContinue, isLast, finishing, finishProgress }) {
  const score = computeDeliveryScore(metrics);
  const scoreColor = score >= 75 ? "text-[#10B981]" : score >= 50 ? "text-[#F59E0B]" : "text-[#E11D48]";

  const Metric = ({ label, value, display, metric }) => {
    const cls = COLOR_CLASSES[getMetricColor(metric, value)];
    return (
      <div className={`border ${cls.border} ${cls.bg} p-3`}>
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mb-1">{label}</div>
        <div className={`font-mono text-lg font-bold ${cls.text}`}>{display}</div>
      </div>
    );
  };

  const mins = Math.floor(metrics.answer_duration_sec / 60);
  const secs = metrics.answer_duration_sec % 60;

  return (
    <div className="rounded-lg border border-[#262626] bg-[#111] p-5 mt-3">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] text-white/40 tracking-[0.25em]">DELIVERY ANALYSIS</div>
          <div className="font-mono text-[10px] text-white/25 mt-0.5">{metrics.word_count} words spoken</div>
        </div>
        <div className={`font-display text-3xl font-black ${scoreColor}`}>
          {score}<span className="text-sm font-normal text-white/30">/100</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <Metric label="Pace" value={metrics.wpm} display={`${metrics.wpm} WPM`} metric="wpm" />
        <Metric label="Filler words" value={metrics.filler_count} display={`${metrics.filler_count}`} metric="filler_count" />
        <Metric label="Longest pause" value={metrics.longest_pause_sec} display={`${metrics.longest_pause_sec}s`} metric="longest_pause_sec" />
        <Metric label="Weak phrases" value={metrics.weak_language_count} display={`${metrics.weak_language_count}`} metric="weak_language_count" />
      </div>
      <Metric label="Duration" value={metrics.answer_duration_sec} display={`${mins}m ${secs}s`} metric="answer_duration_sec" />

      {metrics.filler_words.length > 0 && (
        <div className="mt-3 font-mono text-[10px] text-white/35 leading-relaxed">
          Detected: {metrics.filler_words.map((f) => `"${f.word}" ×${f.count}`).join(" · ")}
        </div>
      )}

      <button
        onClick={onContinue}
        disabled={finishing}
        className="w-full mt-4 py-3 bg-[#0025D8] hover:bg-[#001CB3] disabled:opacity-50 text-white font-mono text-xs uppercase tracking-[0.2em] transition-colors"
      >
        {finishing ? "Grading…" : isLast ? "Finish interview →" : "Continue →"}
      </button>
      {finishProgress?.isRunning && (
        <div className="mt-2">
          <ProgressSteps steps={FINISH_STEPS} currentStep={finishProgress.currentStep} pct={finishProgress.pct} />
        </div>
      )}
    </div>
  );
}

export default function LiveInterview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTestMode = !!user?.is_test;
  const [searchParams] = useSearchParams();
  const retakeModule = searchParams.get("retake");
  const isRetake = !!retakeModule;
  // Per-question retake mode: ?retake_module=behavioral&retake_q=2
  const qRetakeModule = searchParams.get("retake_module");
  const qRetakeQStr = searchParams.get("retake_q");
  const qRetakeQIdx = qRetakeQStr !== null ? parseInt(qRetakeQStr, 10) : null;
  const isQuestionRetake = !!qRetakeModule && qRetakeQIdx !== null && !isNaN(qRetakeQIdx);

  // Pre-screen config (test accounts only — skipped for question retakes)
  const [preScreen, setPreScreen] = useState(isTestMode && !isQuestionRetake);
  const [questionLimit, setQuestionLimit] = useState(3);
  const [videoMode, setVideoMode] = useState(!isTestMode || isQuestionRetake);
  const [sessionStarted, setSessionStarted] = useState(!isTestMode || isQuestionRetake);

  // Interview progress
  const [iv, setIv] = useState(null);
  const [moduleIdx, setModuleIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);

  // Non-avatar answer state
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const finishProgress = useProgressSteps(FINISH_STEPS);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [recording, setRecording] = useState(false);

  // Follow-up state (video/text mock interview mode)
  const [followUp, setFollowUp] = useState(null);
  const [followUpCount, setFollowUpCount] = useState(0);
  const [followUpConvHistory, setFollowUpConvHistory] = useState([]);

  // Avatar state
  const [avatarState, setAvatarState] = useState("intro");
  const [currentMessage, setCurrentMessage] = useState("");
  const [transcript, setTranscript] = useState("");
  const [conversationHistory, setConversationHistory] = useState([]);

  const { speak, cancel: cancelTTS, speaking: ttsSpeaking } = useCartesiaTTS();

  // Delivery analysis state
  const [deliveryCard, setDeliveryCard] = useState(null);
  const [speakingSeconds, setSpeakingSeconds] = useState(0);
  const pendingAdvanceRef = useRef(null);

  // Cheat alert overlay state
  const [cheatAlert, setCheatAlert] = useState(null);
  const cheatAlertTimerRef = useRef(null);

  // Exit / skip state
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

  const handleCheatEvent = useCallback((evt) => {
    clearTimeout(cheatAlertTimerRef.current);
    setCheatAlert(evt);
    cheatAlertTimerRef.current = setTimeout(() => setCheatAlert(null), 3000);
  }, []);

  // Speech delivery tracking refs
  const speechStartRef = useRef(null);
  const totalSpeakingSecRef = useRef(0);
  const longestPauseRef = useRef(0);
  const lastSpeechEndRef = useRef(null);
  const rawTranscriptRef = useRef("");

  // Refs
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recognitionRef = useRef(null);
  const listeningRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const spokenKeyRef = useRef("");
  const pttTranscriptRef = useRef("");
  const avatarStartedRef = useRef(false);

  // Avatar mode: only for test accounts who chose voice on the pre-screen
  // Regular candidates always get the video + follow-up flow
  const isAvatarMode = isTestMode && !isRetake && !isQuestionRetake && iv?.source === "role_based" && videoMode;
  // Mock interview follow-up flow applies to regular candidates (not question retakes — keep single-answer)
  const isFollowUpMode = !isAvatarMode && !isRetake && !isQuestionRetake && iv?.source === "role_based";
  const eyeTrackingEnabled = sessionStarted && videoMode && !!iv && !isAvatarMode;
  const { status, cheatCount, resetEvents } = useEyeTracking(videoRef, { enabled: eyeTrackingEnabled, onEvent: handleCheatEvent });

  useEffect(() => {
    document.documentElement.classList.add("dark-studio");
    return () => document.documentElement.classList.remove("dark-studio");
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/interviews/${id}`);
        if (!isRetake && !isQuestionRetake && data.status === "completed") { navigate(`/interviews/${id}/results`); return; }
        if (isRetake && !data.modules?.[retakeModule]) {
          toast.error("That module is not part of this interview");
          navigate(`/interviews/${id}/results`);
          return;
        }
        if (isQuestionRetake && data.modules?.[qRetakeModule]?.questions?.[qRetakeQIdx] === undefined) {
          toast.error("That question is not part of this interview");
          navigate(`/interviews/${id}/results`);
          return;
        }
        setIv(data);
        if (!isTestMode) {
          track("interview_started", {
            interview_id: id,
            mode: videoMode ? "video" : "text",
            is_retake: isRetake,
            source: data.source,
          });
        }
      } catch { toast.error("Interview not found"); navigate("/dashboard"); }
    })();
  }, [id, navigate, isRetake, retakeModule]); // eslint-disable-line react-hooks/exhaustive-deps

  // Camera setup — skip only for avatar mode (test account voice flow)
  useEffect(() => {
    if (!sessionStarted || !videoMode || !iv) return;
    if (isAvatarMode) return;
    let active = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 1280, height: 720 }, audio: true });
        if (!active) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
        try {
          const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm";
          const rec = new MediaRecorder(stream, { mimeType: mime });
          rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
          rec.start(1000);
          mediaRecorderRef.current = rec;
          setRecording(true);
        } catch {}
      } catch { toast.error("Camera/mic permission required"); }
    })();
    return () => {
      active = false;
      try { mediaRecorderRef.current?.state !== "inactive" && mediaRecorderRef.current?.stop(); } catch {}
      streamRef.current?.getTracks().forEach(t => t.stop());
      cancelTTS();
    };
  }, [sessionStarted, videoMode, iv, isAvatarMode, isRetake, cancelTTS]);

  // ── Avatar helpers ─────────────────────────────────────────────────────────

  const speakAndWait = useCallback(async (text) => {
    setCurrentMessage(text);
    await speak(text);
  }, [speak]);

  // Avatar intro — runs once when iv loads and session starts
  useEffect(() => {
    if (!iv || !isAvatarMode || !sessionStarted || avatarStartedRef.current) return;
    avatarStartedRef.current = true;

    const MODS = MODULE_ORDER.filter(m => iv.modules?.[m]?.questions?.length > 0);
    const firstMod = MODS[0];
    if (!firstMod) return;
    const rawQs = iv.modules[firstMod]?.questions || [];
    const firstModQs = isTestMode ? rawQs.slice(0, questionLimit) : rawQs;
    const firstQ = firstModQs[0];
    if (!firstQ) return;

    const roundNames = MODS.map(m => MODULE_LABEL[m]).join(", ");

    let cancelled = false;
    const runIntro = async () => {
      setAvatarState("speaking");
      const introText = `Hello! I'm Alex, your AI interviewer today. We'll be covering ${roundNames}. Press and hold the microphone button to speak, and release when you're done. Let's begin.`;
      await speakAndWait(introText);
      if (cancelled) return;
      setAvatarState("asking");
      await speakAndWait(firstQ);
      if (cancelled) return;
      setConversationHistory([{ role: "interviewer", text: firstQ }]);
      setAvatarState("ready");
    };

    const t = setTimeout(runIntro, 700);
    return () => { cancelled = true; clearTimeout(t); };
  }, [iv, isAvatarMode, sessionStarted, speakAndWait, isTestMode, questionLimit]);

  const processAnswer = useCallback(async (answerText, curQIdx, curModIdx, curQs, curMODS, curConvHistory, curFollowUpCount) => {
    const newHistory = [...curConvHistory, { role: "candidate", text: answerText }];
    setConversationHistory(newHistory);
    setTranscript("");
    setAvatarState("thinking");

    try {
      const { data } = await api.post(`/interviews/${id}/converse`, {
        module: curMODS[curModIdx],
        question_index: curQIdx,
        conversation_history: newHistory,
        latest_answer: answerText,
        follow_up_count: curFollowUpCount,
        max_follow_ups: 2,
      });

      if (data.action === "follow_up") {
        const fuText = data.follow_up_text;
        setFollowUpCount(f => f + 1);
        setConversationHistory(h => [...h, { role: "interviewer", text: fuText }]);
        setAvatarState("asking");
        await speakAndWait(fuText);
        setAvatarState("ready");
      } else {
        const aggregated = newHistory.filter(t => t.role === "candidate").map(t => t.text).join("\n\n");
        await submitAnswerRef.current(aggregated, curQIdx, curModIdx, curQs, curMODS);
      }
    } catch {
      toast.error("Connection error. Please try again.");
      setAvatarState("ready");
    }
  }, [id, speakAndWait]); // submitAnswer added below via ref

  const submitAnswerRef = useRef(null);

  const submitAnswer = useCallback(async (aggregated, curQIdx, curModIdx, curQs, curMODS) => {
    try {
      const cheats = resetEvents();
      await api.post(`/interviews/${id}/answer`, {
        module: curMODS[curModIdx],
        question_index: curQIdx,
        answer_text: aggregated,
        cheat_events: cheats,
      });

      setConversationHistory([]);
      setFollowUpCount(0);
      const ack = ACKNOWLEDGMENTS[Math.floor(Math.random() * ACKNOWLEDGMENTS.length)];

      if (curQIdx + 1 < curQs.length) {
        const nextQ = curQs[curQIdx + 1];
        setAvatarState("speaking");
        await speakAndWait(`${ack} Next question.`);
        setAvatarState("asking");
        await speakAndWait(nextQ);
        setConversationHistory([{ role: "interviewer", text: nextQ }]);
        setQIdx(curQIdx + 1);
        setAvatarState("ready");
      } else if (curModIdx + 1 < curMODS.length) {
        const nextMod = curMODS[curModIdx + 1];
        const nextRawQs = iv.modules[nextMod]?.questions || [];
        const nextQs = isTestMode ? nextRawQs.slice(0, questionLimit) : nextRawQs;
        const nextQ = nextQs[0];
        setAvatarState("speaking");
        await speakAndWait(`${ack} You've completed the ${MODULE_LABEL[curMODS[curModIdx]]} round. Now let's move to the ${MODULE_LABEL[nextMod]} round.`);
        setAvatarState("asking");
        await speakAndWait(nextQ);
        setConversationHistory([{ role: "interviewer", text: nextQ }]);
        setModuleIdx(curModIdx + 1);
        setQIdx(0);
        setAvatarState("ready");
      } else {
        setAvatarState("done");
        await speakAndWait("Excellent effort! That completes all your interview questions. Let me compile your results now.");
        setFinishing(true); finishProgress.start();
        await api.post(`/interviews/${id}/finish`);
        toast.success("Interview complete");
        navigate(`/interviews/${id}/results`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit answer");
      setAvatarState("ready");
    }
  }, [id, iv, isTestMode, questionLimit, speakAndWait, resetEvents, navigate, finishProgress]);

  submitAnswerRef.current = submitAnswer;

  const startPTTListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Speech not supported. Use Chrome or Edge."); return; }
    cancelTTS();
    pttTranscriptRef.current = "";
    setTranscript("");
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = "en-US";
    rec.onresult = (e) => {
      let final = ""; let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript + " ";
        else interim += e.results[i][0].transcript;
      }
      if (final) pttTranscriptRef.current += final;
      setTranscript(pttTranscriptRef.current + interim);
    };
    rec.onerror = () => {};
    recognitionRef.current = rec;
    try { rec.start(); } catch {}
  }, [cancelTTS]);

  // ── Non-avatar helpers ─────────────────────────────────────────────────────

  const speakQuestion = useCallback((text) => {
    if (!text) return;
    speak(text);
  }, [speak]);

  const MODULES = iv
    ? (isRetake ? [retakeModule] : isQuestionRetake ? [qRetakeModule] : MODULE_ORDER.filter(m => iv.modules?.[m]?.questions?.length > 0))
    : MODULE_ORDER.slice(0, 3);
  const currentModule = MODULES[moduleIdx];
  const allQuestions = isQuestionRetake
    ? [iv?.modules?.[qRetakeModule]?.questions?.[qRetakeQIdx]].filter(Boolean)
    : iv?.modules?.[currentModule]?.questions || [];
  const questions = isTestMode ? allQuestions.slice(0, questionLimit) : allQuestions;
  const question = questions[qIdx];

  useEffect(() => {
    if (!question || !videoMode || isAvatarMode) return;
    const key = `${currentModule}-${qIdx}`;
    if (spokenKeyRef.current === key) return;
    spokenKeyRef.current = key;
    const t = setTimeout(() => speakQuestion(question), 400);
    return () => clearTimeout(t);
  }, [question, currentModule, qIdx, speakQuestion, videoMode, isAvatarMode]);

  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Your browser does not support voice input."); return; }
    cancelTTS();
    // Reset per-session timing on each start
    speechStartRef.current = Date.now();
    lastSpeechEndRef.current = null;

    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = "en-US";

    rec.onspeechstart = () => {
      if (lastSpeechEndRef.current) {
        const pauseSec = (Date.now() - lastSpeechEndRef.current) / 1000;
        if (pauseSec > 0.5) longestPauseRef.current = Math.max(longestPauseRef.current, pauseSec);
        lastSpeechEndRef.current = null;
      }
    };

    rec.onspeechend = () => { lastSpeechEndRef.current = Date.now(); };

    rec.onresult = (e) => {
      let finalChunk = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalChunk += e.results[i][0].transcript + " ";
      }
      if (finalChunk) {
        rawTranscriptRef.current += finalChunk;
        setAnswer((prev) => (prev ? prev + " " : "") + finalChunk.trim());
      }
    };
    rec.onerror = (ev) => {
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        listeningRef.current = false; setListening(false);
        toast.error("Microphone permission denied");
      }
    };
    rec.onend = () => {
      if (listeningRef.current) { try { rec.start(); } catch {} } else { setListening(false); }
    };
    recognitionRef.current = rec;
    listeningRef.current = true;
    try { rec.start(); setListening(true); } catch {}
  };

  const stopListening = () => {
    listeningRef.current = false;
    try { recognitionRef.current?.stop(); } catch {}
    setListening(false);
    // Accumulate this session's speaking time
    if (speechStartRef.current) {
      totalSpeakingSecRef.current += (Date.now() - speechStartRef.current) / 1000;
      speechStartRef.current = null;
    }
  };

  const downloadRecording = () => {
    if (!chunksRef.current.length) { toast.error("No recording yet"); return; }
    const blob = new Blob(chunksRef.current, { type: "video/webm" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `interview-${id}.webm`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  // Live speaking timer shown in the speech input
  useEffect(() => {
    if (!listening) { setSpeakingSeconds(0); return; }
    const t = setInterval(() => setSpeakingSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [listening]);

  // Reset follow-up + delivery tracking whenever the active question changes
  useEffect(() => {
    if (!question) return;
    setFollowUp(null);
    setFollowUpCount(0);
    setFollowUpConvHistory([{ role: "interviewer", text: question }]);
    setDeliveryCard(null);
    speechStartRef.current = null;
    lastSpeechEndRef.current = null;
    longestPauseRef.current = 0;
    totalSpeakingSecRef.current = 0;
    rawTranscriptRef.current = "";
    setSpeakingSeconds(0);
  }, [qIdx, moduleIdx]); // eslint-disable-line

  const nextQuestion = async () => {
    if (!answer.trim()) { toast.error("Please answer before continuing."); return; }
    if (listening) stopListening(); // flushes totalSpeakingSecRef
    setSubmitting(true);

    // Compute delivery for video (non-avatar) mode if the user actually spoke
    const isSpeechMode = videoMode && !isAvatarMode;
    const delivery = (isSpeechMode && rawTranscriptRef.current.trim())
      ? computeDelivery(rawTranscriptRef.current, totalSpeakingSecRef.current, longestPauseRef.current)
      : null;

    try {
      const capturedAnswer = answer.trim(); // capture before state is cleared

      if (!isQuestionRetake) {
        if (isFollowUpMode) {
          const newHistory = [...followUpConvHistory, { role: "candidate", text: capturedAnswer }];
          const { data } = await api.post(`/interviews/${id}/converse`, {
            module: currentModule, question_index: qIdx,
            conversation_history: newHistory, latest_answer: capturedAnswer,
            follow_up_count: followUpCount, max_follow_ups: 2,
          });

          if (data.action === "follow_up") {
            setFollowUpConvHistory([...newHistory, { role: "interviewer", text: data.follow_up_text }]);
            setFollowUpCount((c) => c + 1);
            setFollowUp(data.follow_up_text);
            setAnswer("");
            speakQuestion(data.follow_up_text);
            return;
          }

          const aggregated = newHistory.filter((t) => t.role === "candidate").map((t) => t.text).join("\n\n");
          const cheats = resetEvents();
          await api.post(`/interviews/${id}/answer`, {
            module: currentModule, question_index: qIdx,
            answer_text: aggregated, cheat_events: cheats,
            ...(delivery && { delivery }),
          });
        } else {
          const cheats = resetEvents();
          await api.post(`/interviews/${id}/answer`, {
            module: currentModule, question_index: qIdx,
            answer_text: capturedAnswer, cheat_events: cheats,
            ...(delivery && { delivery }),
          });
        }
      }

      setAnswer("");
      setFollowUp(null);
      setFollowUpCount(0);

      // Capture advance logic at submission time so values are correct via closure
      const capturedQIdx = qIdx;
      const capturedModIdx = moduleIdx;
      const capturedQuestions = questions;
      const capturedMODULES = MODULES;

      const advance = async () => {
        setDeliveryCard(null);
        if (capturedQIdx + 1 < capturedQuestions.length) {
          setQIdx(capturedQIdx + 1);
        } else if (capturedModIdx + 1 < capturedMODULES.length) {
          setModuleIdx(capturedModIdx + 1); setQIdx(0);
        } else {
          setFinishing(true); finishProgress.start();
          try { mediaRecorderRef.current?.state !== "inactive" && mediaRecorderRef.current?.stop(); } catch {}
          cancelTTS();
          if (isQuestionRetake) {
            await api.post(`/interviews/${id}/retake-question`, { module: qRetakeModule, q_index: qRetakeQIdx, answer: capturedAnswer });
            toast.success("Answer submitted — scores updated");
          } else if (isRetake) {
            await api.post(`/interviews/${id}/grade-module`, { module: retakeModule });
            toast.success(`${MODULE_LABEL[retakeModule]} re-graded`);
          } else {
            await api.post(`/interviews/${id}/finish`);
            toast.success("Interview complete");
          }
          navigate(`/interviews/${id}/results`);
        }
      };

      if (delivery) {
        setDeliveryCard(delivery);
        pendingAdvanceRef.current = advance;
      } else {
        await advance();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit answer");
    } finally { setSubmitting(false); }
  };

  const continueFromCard = async () => {
    if (pendingAdvanceRef.current) {
      const fn = pendingAdvanceRef.current;
      pendingAdvanceRef.current = null;
      await fn();
    }
  };

  const performExit = async () => {
    try { mediaRecorderRef.current?.state !== "inactive" && mediaRecorderRef.current?.stop(); } catch {}
    try { window.speechSynthesis.cancel(); } catch {}
    streamRef.current?.getTracks().forEach(t => t.stop());
    if (isQuestionRetake) { navigate(`/interviews/${id}/results`); return; }
    const anyAnswered = qIdx > 0 || moduleIdx > 0;
    track("interview_exit_clicked", {
      interview_id: id,
      question_index: qIdx,
      module: currentModule,
      any_answered: anyAnswered,
    });
    try {
      if (anyAnswered) {
        await api.post(`/interviews/${id}/finish`);
        navigate(`/interviews/${id}/results`);
      } else {
        navigate("/dashboard");
      }
    } catch {
      navigate("/dashboard");
    }
  };

  const skipQuestion = async () => {
    if (listening) stopListening();
    setSubmitting(true);
    track("interview_question_skipped", {
      interview_id: id,
      question_index: qIdx,
      module: currentModule,
    });
    try {
      const cheats = resetEvents();
      await api.post(`/interviews/${id}/answer`, {
        module: currentModule,
        question_index: qIdx,
        answer_text: "[Skipped]",
        cheat_events: cheats,
        skipped: true,
      });
      setAnswer("");
      setFollowUp(null);
      setFollowUpCount(0);

      const capturedQIdx = qIdx;
      const capturedModIdx = moduleIdx;
      const capturedQuestions = questions;
      const capturedMODULES = MODULES;

      if (capturedQIdx + 1 < capturedQuestions.length) {
        setQIdx(capturedQIdx + 1);
        toast.info("Question skipped — scored 0 in your results");
      } else if (capturedModIdx + 1 < capturedMODULES.length) {
        setModuleIdx(capturedModIdx + 1);
        setQIdx(0);
        toast.info("Question skipped — scored 0 in your results");
      } else {
        setFinishing(true); finishProgress.start();
        try { mediaRecorderRef.current?.state !== "inactive" && mediaRecorderRef.current?.stop(); } catch {}
        cancelTTS();
        if (isRetake) {
          await api.post(`/interviews/${id}/grade-module`, { module: retakeModule });
          toast.success(`${MODULE_LABEL[retakeModule]} re-graded`);
        } else {
          await api.post(`/interviews/${id}/finish`);
          toast.success("Interview complete");
        }
        navigate(`/interviews/${id}/results`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to skip question");
    } finally { setSubmitting(false); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!iv) return (
    <div className="min-h-screen bg-[#0F172A] text-white font-mono text-sm flex items-center justify-center">
      Loading session…
    </div>
  );

  // Pre-screen — test accounts only
  if (preScreen && isTestMode) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <div className="font-mono text-[10px] text-muted-foreground tracking-[0.25em] mb-2 flex items-center gap-2">
            <Beaker className="w-3 h-3 text-[#F59E0B]" /> TEST ACCOUNT · SESSION SETUP
          </div>
          <h1 className="font-display font-black text-3xl mb-1">{iv.job_title}</h1>
          <p className="text-muted-foreground text-sm mb-10 font-mono">Configure your session before you begin.</p>

          <div className="mb-8">
            <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-[0.25em] mb-3">Questions per round</div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => setQuestionLimit(n)}
                  className={`w-12 h-12 font-mono font-bold text-lg transition-colors ${
                    questionLimit === n ? "bg-[#0025D8] text-white" : "rounded-xl border border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                  }`}>
                  {n}
                </button>
              ))}
            </div>
            <p className="text-muted-foreground text-[11px] mt-2 font-mono">
              {questionLimit} question{questionLimit !== 1 ? "s" : ""} per round — asked one by one
            </p>
          </div>

          {/* Mode toggle — always shown for test accounts */}
          <div className="mb-10">
            <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-[0.25em] mb-3">Answer mode</div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setVideoMode(false)}
                className={`p-5 border text-left transition-colors ${!videoMode ? "border-[#0025D8] bg-[#0025D8]/5" : "border-border hover:border-foreground"}`}>
                <Type className={`w-5 h-5 mb-3 ${!videoMode ? "text-[#0025D8]" : "text-muted-foreground"}`} />
                <div className="font-semibold text-sm">Text only</div>
                <div className="text-muted-foreground text-xs mt-1">
                  {iv?.source === "role_based" ? "Type answers. No AI voice or camera." : "Type answers. No camera."}
                </div>
              </button>
              <button onClick={() => setVideoMode(true)}
                className={`p-5 border text-left transition-colors ${videoMode ? "border-[#0025D8] bg-[#0025D8]/5" : "border-border hover:border-foreground"}`}>
                <Mic className={`w-5 h-5 mb-3 ${videoMode ? "text-[#0025D8]" : "text-muted-foreground"}`} />
                <div className="font-semibold text-sm">
                  {iv?.source === "role_based" ? "AI Interviewer (voice)" : "Video + Mic"}
                </div>
                <div className="text-muted-foreground text-xs mt-1">
                  {iv?.source === "role_based" ? "Alex speaks questions, push-to-talk answers." : "Camera on, eye tracking active."}
                </div>
              </button>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-[#10B981]/30 bg-[#10B981]/5 px-4 py-3 mb-4">
            <ShieldCheck className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-semibold text-[#10B981]">AI proctoring is active during this session.</span>{" "}
              Face visibility, eye direction, tab switching, window focus, and multiple persons in frame are monitored and logged.
            </p>
          </div>
          <Button onClick={() => {
            setPreScreen(false); setSessionStarted(true);
            track("interview_started", {
              interview_id: id,
              mode: videoMode ? "video" : "text",
              is_retake: isRetake,
            });
          }}
            className="w-full rounded-xl bg-[#0025D8] hover:bg-[#001CB3] py-6 text-base">
            Start Interview →
          </Button>
          <button onClick={() => navigate("/dashboard")}
            className="w-full mt-3 font-mono text-xs text-muted-foreground hover:text-foreground uppercase tracking-[0.2em] transition-colors text-center">
            ← Back to dashboard
          </button>
        </div>
      </main>
    );
  }

  // Progress bar %
  const limitedCount = (m) => isTestMode
    ? Math.min(iv.modules[m]?.questions?.length || 0, questionLimit)
    : (iv.modules[m]?.questions?.length || 0);
  const totalQ = MODULES.reduce((s, m) => s + limitedCount(m), 0);
  const doneQ = MODULES.slice(0, moduleIdx).reduce((s, m) => s + limitedCount(m), 0) + qIdx;
  const pct = totalQ > 0 ? Math.round((doneQ / totalQ) * 100) : 0;
  const frameAlert = status === "warning" || status === "lost";

  const QuestionPills = () => (
    <div className="flex gap-1.5">
      {questions.map((_, i) => (
        <div key={i} className={`w-8 h-8 flex items-center justify-center font-mono text-xs font-bold transition-colors ${
          i === qIdx ? "bg-[#0025D8] text-white"
            : i < qIdx ? "bg-[#262626] text-white/50"
            : "rounded-lg border border-[#333] text-white/25"
        }`}>{i + 1}</div>
      ))}
    </div>
  );

  // ── Avatar UI (Mock Interview) ─────────────────────────────────────────────
  if (isAvatarMode) {
    const ringClass = {
      intro:     "border-white/20",
      speaking:  "border-[#0025D8] animate-pulse",
      asking:    "border-[#0025D8] animate-pulse",
      ready:     "border-white/40",
      recording: "border-[#E11D48] animate-pulse",
      thinking:  "border-white/30 animate-pulse",
      done:      "border-[#10B981]",
    }[avatarState] || "border-white/20";

    const ringGlow = {
      speaking:  { boxShadow: "0 0 50px rgba(0,37,216,0.3)" },
      asking:    { boxShadow: "0 0 50px rgba(0,37,216,0.3)" },
      recording: { boxShadow: "0 0 50px rgba(225,29,72,0.3)" },
      done:      { boxShadow: "0 0 30px rgba(16,185,129,0.2)" },
    }[avatarState] || {};

    const statusLabel = {
      intro:     "Preparing…",
      speaking:  "Speaking…",
      asking:    "Listen carefully",
      ready:     "Your turn — hold to speak",
      recording: "Recording — release when done",
      thinking:  "Thinking…",
      done:      "Interview complete",
    }[avatarState] || "";

    const pttReady = avatarState === "ready";
    const pttActive = avatarState === "recording";

    const handlePTTDown = (e) => {
      e.preventDefault();
      if (!pttReady) return;
      setAvatarState("recording");
      startPTTListening();
    };

    const handlePTTUp = (e) => {
      e.preventDefault();
      if (!pttActive) return;
      try { recognitionRef.current?.stop(); } catch {}
      // Snapshot current state values to pass explicitly (avoids stale closures)
      const snapQIdx = qIdx;
      const snapModIdx = moduleIdx;
      const snapQs = questions;
      const snapMODS = MODULES;
      const snapHistory = conversationHistory;
      const snapFollowUpCount = followUpCount;
      setTimeout(() => {
        const final = pttTranscriptRef.current.trim();
        if (final) {
          processAnswer(final, snapQIdx, snapModIdx, snapQs, snapMODS, snapHistory, snapFollowUpCount);
        } else {
          setAvatarState("ready");
          toast.error("No speech detected. Hold the button and speak clearly.");
        }
      }, 500);
    };

    return (
      <main className="min-h-screen bg-[#0F172A] text-white flex flex-col">
        {/* Top bar */}
        <div className="border-b border-[#262626] px-6 py-3 flex items-center justify-between font-mono text-xs flex-shrink-0">
          <div className="flex items-center gap-4">
            <span className="text-white/40">LIVE · {iv.interview_id.slice(0, 8)}</span>
            <span className="text-white/80">{iv.job_title}</span>
            {isTestMode && (
              <span className="text-[#F59E0B] font-bold uppercase tracking-[0.25em] flex items-center gap-1">
                <Beaker className="w-3 h-3" /> Test mode
              </span>
            )}
          </div>
          <span className="text-white/50 hidden sm:inline font-mono text-[11px]">
            {MODULE_LABEL[currentModule]} · Q{qIdx + 1} of {questions.length}
          </span>
          <div className="flex items-center gap-4">
            <span className="text-white/30 font-mono text-[10px] uppercase tracking-[0.25em]">AI Interviewer</span>
            <button onClick={() => setExitConfirmOpen(true)}
              className="font-mono text-[10px] text-white/25 hover:text-[#E11D48] uppercase tracking-[0.2em] transition-colors">
              Exit
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="h-0.5 bg-[#1a1a1a] flex-shrink-0">
          <div className="h-full bg-[#0025D8] transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>

        {/* Main avatar area */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 gap-7">
          <QuestionPills />

          {/* Avatar circle */}
          <div
            className={`w-44 h-44 rounded-full border-4 flex flex-col items-center justify-center transition-all duration-300 select-none ${ringClass}`}
            style={ringGlow}
          >
            {avatarState === "thinking" ? (
              <Loader2 className="w-9 h-9 text-white/50 animate-spin" />
            ) : avatarState === "recording" ? (
              <Mic className="w-11 h-11 text-[#E11D48]" />
            ) : (
              <>
                <span className="font-display font-black text-4xl leading-none">AI</span>
                <span className="font-mono text-[9px] text-white/35 tracking-[0.25em] mt-1">ALEX</span>
              </>
            )}
          </div>

          {/* Status */}
          <p className="font-mono text-[11px] text-white/40 uppercase tracking-[0.25em] text-center min-h-[18px]">
            {statusLabel}
          </p>

          {/* Message / transcript box */}
          <div className="w-full max-w-xl rounded-lg border border-[#1c1c1c] bg-[#0d0d0d] rounded-xl p-5 min-h-[90px] flex items-start">
            {transcript && pttActive ? (
              <p className="text-white/55 text-sm leading-relaxed italic">{transcript}</p>
            ) : currentMessage ? (
              <p className="text-white/85 text-sm leading-relaxed">{currentMessage}</p>
            ) : (
              <p className="text-white/15 text-sm italic">Waiting…</p>
            )}
          </div>

          {/* Push-to-talk button */}
          <div className="flex flex-col items-center gap-3">
            <button
              onMouseDown={handlePTTDown}
              onMouseUp={handlePTTUp}
              onMouseLeave={(e) => { if (pttActive) handlePTTUp(e); }}
              onTouchStart={handlePTTDown}
              onTouchEnd={handlePTTUp}
              disabled={!pttReady && !pttActive}
              className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-150 select-none touch-none ${
                pttActive
                  ? "bg-[#E11D48] scale-110"
                  : pttReady
                  ? "bg-[#0025D8] hover:bg-[#1a3fff] hover:scale-105 cursor-pointer"
                  : "bg-[#1a1a1a] cursor-not-allowed opacity-30"
              }`}
              style={pttActive ? { boxShadow: "0 0 35px rgba(225,29,72,0.45)" } : pttReady ? { boxShadow: "0 0 25px rgba(0,37,216,0.35)" } : {}}
            >
              <Mic className="w-8 h-8 text-white" />
            </button>
            <p className="font-mono text-[10px] text-white/25 uppercase tracking-[0.2em]">
              {pttActive ? "Release when done" : pttReady ? "Hold to speak" : "Please wait…"}
            </p>
            {pttReady && !isQuestionRetake && (
              <button onClick={skipQuestion} disabled={submitting}
                className="mt-2 font-mono text-[10px] text-white/20 hover:text-white/50 uppercase tracking-[0.2em] transition-colors disabled:opacity-30">
                Skip question
              </button>
            )}
          </div>
        </div>

        {/* Exit confirmation overlay */}
        {exitConfirmOpen && <ExitConfirmOverlay qIdx={qIdx} moduleIdx={moduleIdx} onStay={() => setExitConfirmOpen(false)} onExit={performExit} />}
      </main>
    );
  }

  // ── Non-avatar UI (Custom Interview / Retake) ──────────────────────────────
  return (
    <main className="min-h-screen bg-[#0F172A] text-white">
      <div className="border-b border-[#262626] px-6 py-3 flex items-center justify-between font-mono text-xs">
        <div className="flex items-center gap-4">
          <span className="text-white/40">
            {isTestMode ? `TEST · ${videoMode ? "VIDEO" : "TEXT-ONLY"}` : (isRetake || isQuestionRetake ? "RETAKE" : "LIVE")} · {iv.interview_id.slice(0, 8)}
          </span>
          <span className="text-white/80">{iv.job_title}</span>
          {isTestMode && <span className="text-[#F59E0B] font-bold uppercase tracking-[0.25em] flex items-center gap-1"><Beaker className="w-3 h-3" /> Test mode</span>}
          {isRetake && !isQuestionRetake && <span className="text-[#0025D8] font-bold uppercase tracking-[0.25em]">Retaking {MODULE_LABEL[retakeModule]}</span>}
          {isQuestionRetake && <span className="text-[#7C3AED] font-bold uppercase tracking-[0.25em]">Retaking Q{qRetakeQIdx + 1} · {MODULE_LABEL[qRetakeModule]}</span>}
        </div>
        <div className="flex items-center gap-6">
          {(() => {
            const hasStandard = MODULES.some(m => m !== "custom");
            const hasCustom = MODULES.includes("custom");
            const inCustom = currentModule === "custom";
            const phases = [];
            if (hasStandard) phases.push({ key: "standard", label: "Standard · HR / Tech / Behavior" });
            if (hasCustom) phases.push({ key: "custom", label: "Custom" });
            return phases.map(p => {
              const active = (p.key === "standard" && !inCustom) || (p.key === "custom" && inCustom);
              const passed = p.key === "standard" && inCustom;
              return (
                <span key={p.key} className={`uppercase tracking-[0.25em] ${active ? "text-[#0025D8]" : passed ? "text-white/80" : "text-white/30"}`}>
                  {passed && <Check className="inline w-3 h-3 mr-1" />}{p.label}
                </span>
              );
            });
          })()}
          <span className="text-white/40 hidden xl:inline">→</span>
          <span className="text-white/60 hidden xl:inline">{MODULE_LABEL[currentModule]} · {qIdx + 1}/{questions.length}</span>
        </div>
        <div className="flex items-center gap-4">
          {!videoMode ? (
            <span className="text-white/40 font-mono text-[10px] uppercase tracking-[0.25em]">No proctoring</span>
          ) : (
            <>
              <span className="text-white/40">CHEATS</span>
              <span className={`font-bold ${cheatCount > 0 ? "text-[#E11D48]" : "text-white/80"}`} data-testid="cheat-count">{cheatCount}</span>
            </>
          )}
          <button onClick={() => setExitConfirmOpen(true)}
            className="font-mono text-[10px] text-white/25 hover:text-[#E11D48] uppercase tracking-[0.2em] transition-colors ml-2">
            Exit
          </button>
        </div>
      </div>

      <div className="h-0.5 bg-[#262626]"><div className="h-full bg-[#0025D8] transition-all" style={{ width: `${pct}%` }} /></div>

      {!videoMode ? (
        <div className="max-w-3xl mx-auto p-6">
          <div className="mb-4"><QuestionPills /></div>
          <div className="rounded-lg border border-[#262626] bg-[#111] p-6 mb-0">
            <div className="font-mono text-[10px] text-white/40 tracking-[0.25em] mb-2">
              {isQuestionRetake ? `RETAKE Q${qRetakeQIdx + 1} · ${MODULE_LABEL[qRetakeModule]}` : `QUESTION ${qIdx + 1} · ${MODULE_LABEL[currentModule]}`}
            </div>
            <h2 className="font-display font-bold text-2xl leading-snug" data-testid="current-question">{question}</h2>
          </div>
          {followUp && (
            <div className="border-x border-b border-[#7C3AED]/50 bg-[#7C3AED]/8 p-4 mb-0">
              <div className="font-mono text-[10px] text-[#7C3AED] tracking-[0.25em] mb-1.5">FOLLOW-UP</div>
              <p className="text-sm text-white/90 leading-relaxed">{followUp}</p>
            </div>
          )}
          <Textarea value={answer} onChange={e => setAnswer(e.target.value)}
            placeholder={followUp ? "Answer the follow-up question…" : "Paste or type your answer here…"}
            className="rounded-xl bg-[#111] border-[#262626] text-white min-h-[240px] mb-4 mt-4"
            data-testid="answer-input" autoFocus />
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-5">
              <div className="font-mono text-xs text-white/40">
                {qIdx + 1} / {questions.length} · {MODULE_LABEL[currentModule]}
                {MODULES.length > 1 && <span> · phase {moduleIdx + 1}/{MODULES.length}</span>}
              </div>
              {!followUp && (
                <button onClick={skipQuestion} disabled={submitting || finishing}
                  className="font-mono text-[11px] text-white/25 hover:text-white/55 uppercase tracking-[0.2em] transition-colors disabled:opacity-30">
                  Skip
                </button>
              )}
            </div>
            <Button onClick={nextQuestion} disabled={submitting || finishing}
              className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3] py-6 px-8 text-base" data-testid="submit-answer">
              {finishing ? "Grading…"
                : submitting ? (isFollowUpMode ? "Thinking…" : "Submitting…")
                : followUp ? "Answer follow-up →"
                : (qIdx + 1 === questions.length && moduleIdx + 1 === MODULES.length ? "Finish & grade →" : "Next question →")}
            </Button>
            {finishProgress.isRunning && (
              <ProgressSteps steps={FINISH_STEPS} currentStep={finishProgress.currentStep} pct={finishProgress.pct} />
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6">
          <div className="lg:col-span-8">
            <div className={`relative border ${frameAlert ? "border-[#E11D48] alert-pulse" : "border-[#262626]"} bg-[#0F172A] aspect-video overflow-hidden`}>
              <video ref={videoRef} muted playsInline className="w-full h-full object-cover" data-testid="live-video" />
              <div className="absolute top-3 left-3 font-mono text-[10px] text-white/60 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rec-dot" /> REC
              </div>
              {frameAlert && !cheatAlert && (
                <div className="absolute top-3 right-3 font-mono text-xs text-[#E11D48] flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {status === "lost" ? "FACE NOT VISIBLE" : status === "looking_down" ? "EYES DOWN" : "ATTENTION DRIFT"}
                </div>
              )}
              {/* Interruptive cheat alert overlay */}
              {cheatAlert && (
                <div className="absolute inset-0 bg-[#0F172A]/80 flex items-center justify-center z-10 pointer-events-none">
                  <div className="bg-[#E11D48] px-6 py-5 text-white text-center max-w-xs mx-4">
                    <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
                    <div className="font-mono text-xs font-bold uppercase tracking-[0.25em] mb-1">
                      {CHEAT_MESSAGES[cheatAlert.type]?.label || "VIOLATION DETECTED"}
                    </div>
                    <div className="font-mono text-[11px] text-white/80 leading-relaxed">
                      {CHEAT_MESSAGES[cheatAlert.type]?.detail || "This has been logged."}
                    </div>
                  </div>
                </div>
              )}
              <div className="absolute bottom-3 left-3 right-3 flex justify-between font-mono text-[10px] text-white/50">
                <span>GAZE · {status.toUpperCase()}</span>
                <span>{MODULE_LABEL[currentModule]} · {qIdx + 1}/{questions.length}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-4 flex-wrap">
              <Button variant="outline" onClick={() => { if (question) speakQuestion(question); }} className="rounded-xl border-[#262626] bg-transparent text-white hover:bg-[#171717]">
                <Volume2 className="w-4 h-4 mr-2" />{ttsSpeaking ? "AI speaking…" : "Replay question"}
              </Button>
              <Button variant="outline" onClick={downloadRecording} className="rounded-xl border-[#262626] bg-transparent text-white hover:bg-[#171717]">
                <Download className="w-4 h-4 mr-2" /> Save recording
              </Button>
              <div className="flex items-center gap-2 font-mono text-xs text-white/50 ml-auto">
                <Video className="w-4 h-4" />{recording ? "Recording · on-device" : "Camera only"}
              </div>
            </div>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-0">
            <div className="mb-3"><QuestionPills /></div>
            <div className="rounded-lg border border-[#262626] bg-[#111] p-5">
              <div className="font-mono text-[10px] text-white/40 tracking-[0.25em] mb-2">
                {isQuestionRetake ? `RETAKE Q${qRetakeQIdx + 1} · ${MODULE_LABEL[qRetakeModule]}` : `QUESTION ${qIdx + 1}`}
              </div>
              <h2 className="font-display font-bold text-lg leading-snug" data-testid="current-question">{question}</h2>
            </div>
            {followUp && (
              <div className="border-x border-b border-[#7C3AED]/50 bg-[#7C3AED]/8 p-4">
                <div className="font-mono text-[10px] text-[#7C3AED] tracking-[0.25em] mb-1.5">FOLLOW-UP</div>
                <p className="text-sm text-white/90 leading-relaxed">{followUp}</p>
              </div>
            )}

            {deliveryCard ? (
              <DeliveryCard
                metrics={deliveryCard}
                onContinue={continueFromCard}
                isLast={qIdx + 1 === questions.length && moduleIdx + 1 === MODULES.length}
                finishing={finishing}
                finishProgress={finishProgress}
              />
            ) : (
              <>
                {/* Speech-first answer input */}
                <div className="rounded-lg border border-[#262626] bg-[#0F172A] mt-3 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${listening ? "bg-[#E11D48] animate-pulse" : "bg-[#444]"}`} />
                    <span className="font-mono text-[10px] text-white/40 uppercase tracking-[0.2em] flex-1">
                      {listening ? `Listening… ${speakingSeconds}s` : "Mic ready"}
                    </span>
                  </div>
                  <Textarea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder={listening
                      ? "Speaking… transcript appears here"
                      : followUp
                        ? "Tap mic, speak your follow-up answer…"
                        : "Tap mic to speak, or type your answer…"}
                    className="rounded-xl bg-[#111] border-[#333] text-white min-h-[130px] mb-3 text-sm"
                    data-testid="answer-input"
                  />
                  <Button
                    onClick={() => listening ? stopListening() : startListening()}
                    className={`w-full rounded-xl py-3 ${listening ? "bg-[#E11D48] hover:bg-[#BE123C]" : "bg-[#1a1a1a] hover:bg-[#262626] rounded-lg border border-[#333]"} text-white`}
                  >
                    {listening
                      ? <><MicOff className="w-4 h-4 mr-2" /> Stop speaking</>
                      : <><Mic className="w-4 h-4 mr-2" /> {answer ? "Continue speaking" : "Speak answer"}</>}
                  </Button>
                </div>
                <Button onClick={nextQuestion} disabled={submitting || finishing}
                  className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3] py-6 text-base mt-3" data-testid="submit-answer">
                  {finishing ? "Grading…"
                    : submitting ? (isFollowUpMode ? "Thinking…" : "Submitting…")
                    : followUp ? "Answer follow-up →"
                    : (qIdx + 1 === questions.length && moduleIdx + 1 === MODULES.length ? "Finish interview →" : "Next question →")}
                </Button>
                {finishProgress.isRunning && (
                  <ProgressSteps steps={FINISH_STEPS} currentStep={finishProgress.currentStep} pct={finishProgress.pct} />
                )}
                {!followUp && !isQuestionRetake && (
                  <button onClick={skipQuestion} disabled={submitting || finishing}
                    className="w-full mt-2 font-mono text-[10px] text-white/20 hover:text-white/50 uppercase tracking-[0.2em] transition-colors disabled:opacity-30 text-center">
                    Skip question
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {exitConfirmOpen && <ExitConfirmOverlay qIdx={qIdx} moduleIdx={moduleIdx} onStay={() => setExitConfirmOpen(false)} onExit={performExit} />}
    </main>
  );
}
